import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { Icon } from '@noths/polaris-design-system-core-components';

import type { ReviewsSummaryRatingBreakdown } from 'src/redux/reviews/types';
import { NO_RATINGS, ratingRowLabel } from '../constants/copy';
import * as styles from './RatingHistogram.styles';

/** Highest first, which is how a rating breakdown is read. */
const STARS = [5, 4, 3, 2, 1] as const;

export interface RatingHistogramProps {
  /** How many reviews sit at each star, 1 to 5. */
  starCounts: ReviewsSummaryRatingBreakdown['starCounts'];
}

/**
 * How a product's reviews spread across the five stars. Bars are scaled against the sum
 * of the counts, not Feefo's separately reported total, which can disagree with it.
 * Display only; the star filter is its own control.
 */
export const RatingHistogram = ({ starCounts }: RatingHistogramProps) => {
  const total = STARS.reduce((sum, star) => sum + (starCounts[star] || 0), 0);

  if (total === 0) {
    return <p css={styles.empty}>{NO_RATINGS}</p>;
  }

  return (
    <ul css={styles.rows} data-testid="rating-histogram">
      {STARS.map((star) => {
        const count = starCounts[star] || 0;

        return (
          <li css={styles.row} key={star}>
            {/* One phrase per row for screen readers; the cells below are hidden. */}
            <span css={visuallyHidden}>{ratingRowLabel(star, count)}</span>
            <span aria-hidden css={styles.star}>
              {star}
              <Icon name="starFull" size="sm" />
            </span>
            <span aria-hidden css={styles.track}>
              <span
                css={styles.bar}
                style={
                  { [styles.BAR_WIDTH_VAR]: `${(count / total) * 100}%` } as React.CSSProperties
                }
              />
            </span>
            <span aria-hidden css={styles.count}>
              {count}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

RatingHistogram.displayName = 'RatingHistogram';
